import React, { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { JiraIcon } from "@atlaskit/logo";
import { Box, Grid, Inline, Stack, media, xcss } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import { ProductKeys, Editions } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import Button from "@/components/wpl/button/src";
import { Picture, PictureProps } from "@/components/wpl/image/src";
import TabGroup from "@/components/wpl/tab-group/src";
import { useLocalizedHref } from "@/libraries/web-framework";

import { messages } from "./messages";

// Styles for the content tab component
const TabContentStyles = xcss({
    minHeight: "fit-content",
    maxWidth: "1200px",
    marginInline: "auto",
    gridTemplateColumns: "1fr",
    [media.above.sm]: {
        gridTemplateColumns: "340px 1fr"
    },
    [media.above.md]: {
        gridTemplateColumns: "400px 1fr"
    }
});

const TabContentLeft = xcss({
    order: "2",
    paddingInline: "space.400",
    paddingBlock: "space.200",
    [media.above.sm]: {
        order: "1"
    },
    [media.above.lg]: {
        paddingInline: "space.0"
    }
});

const TabContentRight = css({
    "order": "1",
    "aspectRatio": "84/83",
    "opacity": "0",
    "animation": "fadeIn 0.3s ease 0.3s forwards",
    "@keyframes fadeIn": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: "0"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: "1"
        }
    },
    [media.above.sm]: {
        order: "2",
        aspectRatio: "681/400"
    }
});

const TabContentLeftHeadingStyles = xcss({
    fontWeight: "700",
    [media.above.md]: {
        fontSize: "32px",
        lineHeight: "38px"
    }
});

const TabContentListStyles = xcss({
    listStyle: "none",
    paddingInlineStart: "space.0",
    marginBlockStart: "space.0",
    marginBlockEnd: "space.400"
});

const TabContentListItemTextStyles = xcss({
    fontSize: "16px",
    lineHeight: "24px",
    paddingInlineEnd: "space.0",
    [media.above.md]: {
        fontSize: "20px",
        lineHeight: "28px"
    }
});

const TabContentRightPictureStyles = css({
    aspectRatio: "131/128",

    paddingBlockEnd: token("space.200", "16px"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        objectFit: "cover",
        aspectRatio: "131/128",
        [media.above.sm]: {
            aspectRatio: "19/11"
        }
    }
});

const TabContentLeftCtaStyles = css({
    "marginInline": "auto",
    "borderRadius": "24px",
    "paddingInline": `${token("space.300", "20px")} !important`, // This is needed to override the padding-left and padding-right from the Button component. We should update the Button component to set padding-inline instead of padding-left and padding-right.
    "background": token("color.background.brand.bold", "#0C66E4"),
    "minHeight": "48px",
    "marginBlockStart": token("space.200"),
    "fontSize": "20px",
    ":hover": {
        background: token("color.background.brand.boldest", "#123263")
    },
    [media.above.sm]: {
        marginInlineStart: "0"
    }
});
const TabContentRightCtaStylestest = css({
    "marginInline": "auto",
    "borderRadius": "24px",
    "paddingInline": `${token("space.300", "20px")} !important`, // This is needed to override the padding-left and padding-right from the Button component. We should update the Button component to set padding-inline instead of padding-left and padding-right.
    "background": "transparent",
    "border": "transparent",
    "minHeight": "48px",
    "marginBlockStart": token("space.200"),
    "fontSize": "20px",
    "color": "#0C66E4",
    ":hover": {
        textDecoration: "underline",
        background: "transparent"
    },
    ":focus": {
        background: "transparent"
    },
    ":active": {
        background: "transparent"
    },
    [media.above.sm]: {
        marginInlineStart: "0"
    }
});

const tryNowButtonMessage = {
    id: "jsm-use-case-hero-tabs.try-now",
    defaultMessage: "Try now"
};

const joinADemoButtonMessage = {
    id: "jsm-use-case-hero-tabs.join-a-demo",
    defaultMessage: "Join a demo"
};

// Component for the content of each tab, has a heading, a picture and a list of items with icons
const HeroTabContent = ({
    heading,
    picture,
    list,
    imageAlt
}: {
    heading: {
        id: string;
        defaultMessage: string;
    };
    list: {
        icon: ReactNode;
        text: {
            id: string;
            defaultMessage: string;
        };
    }[];
    picture: PictureProps;
    imageAlt?: {
        id: string;
        defaultMessage: string;
    };
}) => {
    const { formatMessage } = useIntl();
    const signupLink = useLocalizedHref(
        "/software/jira/service-management/free"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_SERVICE_DESK,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });
    return (
        <Grid gap="space.500" xcss={TabContentStyles}>
            <Box xcss={TabContentLeft}>
                <Box paddingBlockEnd="space.300">
                    <Heading as="h3" level="h700">
                        <Box as="span" xcss={TabContentLeftHeadingStyles}>
                            {formatMessage(heading)}
                        </Box>
                    </Heading>
                </Box>
                <Stack as="ul" space="space.200" xcss={TabContentListStyles}>
                    {list.map((item) => (
                        <Inline
                            as="li"
                            key={item.text.id}
                            alignBlock="center"
                            space="space.100"
                        >
                            {item.icon}
                            <Box as="span" xcss={TabContentListItemTextStyles}>
                                {formatMessage(item.text)}
                            </Box>
                        </Inline>
                    ))}
                    <div className="tab-content-ctas">
                        <Button
                            css={TabContentLeftCtaStyles}
                            onClick={openCrossFlow}
                            url={signupLink}
                            text={formatMessage(tryNowButtonMessage)}
                            aria-label={tryNowButtonMessage}
                            dataEventProps={{
                                "data-product-key": "jiraServiceManagement",
                                "data-event-component": "linkButton",
                                "data-event-container": "heroSection",
                                "data-event": "clicked",
                                "data-label":
                                    formatMessage(tryNowButtonMessage),
                                "data-label-english":
                                    tryNowButtonMessage.defaultMessage,
                                "data-track-event": heading.defaultMessage
                            }}
                        />
                        <Button
                            url={useLocalizedHref(
                                "/software/jira/service-management/demo"
                            )}
                            text={formatMessage(joinADemoButtonMessage)}
                            css={TabContentRightCtaStylestest}
                            aria-label={formatMessage(joinADemoButtonMessage)}
                            type="secondary"
                            className="button-join-demo"
                            dataEventProps={{
                                "data-product-key": "jiraServiceManagement",
                                "data-event-component": "linkButton",
                                "data-event-container": "heroSection",
                                "data-event": "clicked",
                                "data-label": formatMessage(
                                    joinADemoButtonMessage
                                ),
                                "data-label-english": formatMessage(
                                    joinADemoButtonMessage
                                )
                            }}
                        />
                    </div>
                </Stack>
            </Box>
            <div css={TabContentRight}>
                <Picture
                    {...picture}
                    image={{
                        src: picture.image.src,
                        alt: imageAlt
                            ? formatMessage(imageAlt)
                            : picture.image.alt
                    }}
                    css={TabContentRightPictureStyles}
                />
            </div>
        </Grid>
    );
};

const iconDimensions = xcss({ width: "48px", height: "48px" });

// Content for each tab
const tabs = [
    {
        id: "it-support",
        textLabel: "IT support",
        dataLabelEnglish: "IT supporty",
        component: (
            <HeroTabContent
                heading={messages.jsmUseCaseHeroTabsItSupportHeading}
                imageAlt={messages.jsmUseCaseHeroTabsItSupportAlt}
                picture={{
                    image: {
                        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/ds5q16kwob38rb02h534e0dp37830mi7.webp",
                        alt: "A list of 4 service requests with fields for summary, status, requestor, channel and response. There is a search bar above the list of requests."
                    },
                    sources: [
                        {
                            srcSet: "https://dam-cdn.atl.orangelogic.com/AssetLink/0386o60lcs23u0wxo60c181728fvqa65.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: messages.jsmUseCaseHeroTabsItSupportUsp1,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1_3)">
                                        <path
                                            d="M26.2857 13.7143L29.7212 13.7143V9.14286H18.279L18.279 13.7143L21.7143 13.7143V16.2073C15.8626 17.2822 11.4286 22.409 11.4286 28.5714V29.7143H36.5714V28.5714C36.5714 22.409 32.1374 17.2822 26.2857 16.2073V13.7143Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M38.8571 33.1429V37.7143L9.14286 37.7143V33.1429L38.8571 33.1429Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1_3">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsItSupportUsp2,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_2_3)">
                                        <path
                                            d="M32 20.5714L16 20.5714L16 16L32 16V20.5714Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8 8V40H40V8H8ZM35.4286 12.5714H12.5714V24H17.1429C17.1429 27.7871 20.2129 30.8571 24 30.8571C27.7871 30.8571 30.8571 27.7871 30.8571 24H35.4286V12.5714Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2_3">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsItSupportUsp3,
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({
                                    width: "48px",
                                    height: "48px"
                                })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9.14286 28.5714L27.4286 3.42857V19.4286H38.8571L20.5714 44.5714V28.5714H9.14286Z"
                                        fill="#1868DB"
                                    />
                                </svg>
                            </Box>
                        )
                    }
                ]}
            />
        )
    },
    {
        id: "engineering-support",
        textLabel: "Engineering support",
        dataLabelEnglish: "Engineering support",
        component: (
            <HeroTabContent
                heading={messages.jsmUseCaseHeroTabsEngineeringSupportHeading}
                imageAlt={messages.jsmUseCaseHeroTabsEngineeringSupportAlt}
                picture={{
                    image: {
                        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/h7r143ki88j2v6n656787rn8gc64yph1.webp",
                        alt: "A service project with options to see your queue, raise a request or see a knowledge base. One request is open. Its title is ‘unhelpful error message’ and has details about who raised the request and what it’s about, as well as any linked requests."
                    },
                    sources: [
                        {
                            srcSet: "https://dam-cdn.atl.orangelogic.com/AssetLink/7v45j3bosu45w2lq477u15tjfem5783e.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: messages.jsmUseCaseHeroTabsEngineeringSupportUsp1,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_2_4)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M40 8H24L4.57143 27.4286L20.5714 43.4286L40 24V8ZM30.8571 14.8571C29.5948 14.8571 28.5714 15.8805 28.5714 17.1429C28.5714 18.4052 29.5948 19.4286 30.8571 19.4286C32.1195 19.4286 33.1429 18.4052 33.1429 17.1429C33.1429 15.8805 32.1195 14.8571 30.8571 14.8571Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2_4">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsEngineeringSupportUsp2,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_5_26225)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M19.4291 35.4279L40.0006 35.4275V7.99888L8.00056 7.99933V42.2846L19.4291 35.4279ZM33.1429 19.4286H14.8571V14.8571H33.1429V19.4286ZM25.1429 28.5714H14.8571V24H25.1429V28.5714Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5_26225">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsEngineeringSupportUsp3,
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({
                                    width: "48px",
                                    height: "48px"
                                })}
                            >
                                <JiraIcon size="large" appearance="brand" />
                            </Box>
                        )
                    }
                ]}
            />
        )
    },
    {
        id: "employee-support",
        textLabel: "Employee support",
        dataLabelEnglish: "Employee support",
        component: (
            <HeroTabContent
                heading={messages.jsmUseCaseHeroTabsEmployeeSupportHeading}
                imageAlt={messages.jsmUseCaseHeroTabsEmployeeSupportAlt}
                picture={{
                    image: {
                        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/iejok30hpqe58a1ys00fwn86a4ho034m.webp",
                        alt: "A request portal with buttons for each company function to enter their part of the portal. For example, ‘human resources - employee help’ and ‘Facilities - book an event’."
                    },
                    sources: [
                        {
                            srcSet: "https://dam-cdn.atl.orangelogic.com/AssetLink/56m2ndu02rfu3qa17o7yl2i13t0628s0.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: messages.jsmUseCaseHeroTabsEmployeeSupportUsp1,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_5_26235)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M19.4291 35.4279L40.0006 35.4275V7.99888L8.00056 7.99933V42.2846L19.4291 35.4279ZM33.1429 19.4286H14.8571V14.8571H33.1429V19.4286ZM25.1429 28.5714H14.8571V24H25.1429V28.5714Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5_26235">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsEmployeeSupportUsp2,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_5_26238)">
                                        <path
                                            d="M33.1429 19.4286H24V14.8571H33.1429V19.4286Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M30.8571 27.4286H24V22.8571H30.8571V27.4286Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M17.1429 8V14.8571H8V33.7143C8 37.1858 10.8142 40 14.2857 40H33.1429C36.9299 40 40 36.9299 40 33.1429V8H17.1429ZM36.5714 11.4286H20.5714V33.7143C20.5714 34.7431 20.3243 35.7142 19.886 36.5714H33.1429C35.0364 36.5714 36.5714 35.0364 36.5714 33.1429V11.4286Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5_26238">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsEmployeeSupportUsp3,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_5_26241)">
                                        <path
                                            d="M26.9551 17.8125L37.2408 7.52679L40.4733 10.7593L30.1876 21.045L26.9551 17.8125Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M15.8037 12.5714L26.2856 23.0534V40.0002H21.7142V24.9469L12.5711 15.8039L12.5712 23.2157H7.99972V8.00002L23.2154 8.00001L23.2154 12.5714L15.8037 12.5714Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5_26241">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    }
                ]}
            />
        )
    },
    {
        id: "it-operations",
        textLabel: "IT operations",
        dataLabelEnglish: "IT operations",
        component: (
            <HeroTabContent
                heading={messages.jsmUseCaseHeroTabsItOperationHeading}
                imageAlt={messages.jsmUseCaseHeroTabsItOperationAlt}
                picture={{
                    image: {
                        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/b5db764r7xdku57120or431jpot8843n.webp",
                        alt: "A request called ‘Access service on staging’ with requestor name (Andrew) and details about the request. There is also a risk summary and an option to either ‘decline’ or ‘authorize’ Andrew’s request."
                    },
                    sources: [
                        {
                            srcSet: "https://dam-cdn.atl.orangelogic.com/AssetLink/27g7egubc3xn45a4r7e0t26d4q2p23r1.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: messages.jsmUseCaseHeroTabsItOperationUsp1,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_6_2)">
                                        <path
                                            d="M9.14286 28.5714L27.4286 3.42857V19.4286H38.8571L20.5714 44.5714V28.5714H9.14286Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_6_2">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsItOperationUsp2,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_6_5)">
                                        <path
                                            d="M24 16L13.2273 26.7727C10.815 25.6499 9.14276 23.2043 9.14286 20.3681C9.14296 17.2863 11.142 14.5606 14.0814 13.6345L15.7552 13.1071L16.2039 11.8349C17.2558 8.8521 20.0747 6.85714 23.2376 6.85714C26.6222 6.85714 29.5824 9.13624 30.4479 12.4083L31.1837 15.1905H33.8809C37.2604 15.1905 40 17.9301 40 21.3095C40 24.2111 37.9804 26.641 35.2702 27.2702L24 16Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M32.4736 29.2407L29.2411 32.4732L26.2858 29.5179V40H21.7144V29.5181L18.7593 32.4732L15.5268 29.2407L24.0002 20.7673L32.4736 29.2407Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_6_5">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsItOperationUsp3,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_6_8)">
                                        <path
                                            d="M16.9388 22.8571L21.7143 8H26.2857L31.0612 22.8571H16.9388Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M15.4694 27.4286L12.898 35.4286H9.14286V40H38.8571V35.4286H35.102L32.5306 27.4286H15.4694Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_6_8">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    }
                ]}
            />
        )
    },
    {
        id: "customer-service",
        textLabel: "Customer service",
        dataLabelEnglish: "Customer service",
        component: (
            <HeroTabContent
                heading={messages.jsmUseCaseHeroTabsCustomerServiceHeading}
                imageAlt={messages.jsmUseCaseHeroTabsCustomerServiceAlt}
                picture={{
                    image: {
                        src: "https://dam-cdn.atl.orangelogic.com/AssetLink/qg16kaywc0437wuidqu2bjp63eot75ys.webp",
                        alt: "A company request portal with button for each function to raise their own requests. There is a panel open that has options to customize the look and feel of the portal, including its name and logo."
                    },
                    sources: [
                        {
                            srcSet: "https://dam-cdn.atl.orangelogic.com/AssetLink/0m8t568in48dx65h8dp781os3f8b53r7.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: messages.jsmUseCaseHeroTabsCustomerServiceUsp1,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_6_11)">
                                        <path
                                            d="M23.3596 14.3508L9.4258 28.2845L7.36747 40.6292L19.7137 38.5724L33.6456 24.6405L23.3596 14.3508Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M36.0699 22.2161L39.1145 19.1716C40.8997 17.3863 40.8997 14.4919 39.1145 12.7066L35.2916 8.88372C33.5063 7.09847 30.6119 7.09847 28.8266 8.88372L25.784 11.9264L36.0699 22.2161Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_6_11">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsCustomerServiceUsp2,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_6_14)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M34.1099 39.2391C39.0378 35.9633 42.2857 30.3609 42.2857 24C42.2857 13.9011 34.0989 5.71429 24 5.71429C13.9011 5.71429 5.71429 13.9011 5.71429 24C5.71429 30.3609 8.96221 35.9633 13.8901 39.2391C14.783 34.468 18.9698 30.8571 24 30.8571C29.0302 30.8571 33.217 34.468 34.1099 39.2391ZM24 27.4286C27.7871 27.4286 30.8571 24.3585 30.8571 20.5714C30.8571 16.7843 27.7871 13.7143 24 13.7143C20.2129 13.7143 17.1429 16.7843 17.1429 20.5714C17.1429 24.3585 20.2129 27.4286 24 27.4286Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_6_14">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: messages.jsmUseCaseHeroTabsCustomerServiceUsp3,
                        icon: (
                            <Box as="span" xcss={iconDimensions}>
                                <JiraIcon size="large" appearance="brand" />
                            </Box>
                        )
                    }
                ]}
            />
        )
    }
];

const useTabs = () => {
    return React.useMemo(() => tabs, []);
};

// Here's the overrides that we need to make the component work with the new design.

const HeroTabStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".select-container": {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role=tablist]": {
        display: "flex",
        overflow: "auto"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role=tablist]::-webkit-scrollbar": {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role=tablist] .tab-group-inner": {
        flexWrap: "nowrap",
        whiteSpace: "nowrap",
        justifyContent: "flex-start",
        marginBlockEnd: token("space.400", "32px"),
        [media.above.sm]: {
            justifyContent: "center"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role=tablist] .tab-group-inner button": {
        fontSize: "16px",
        lineHeight: "24px",
        [media.above.md]: {
            fontSize: "20px",
            lineHeight: "28px"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role=tablist] .tab-group-inner button:last-child": {
        marginInlineEnd: token("space.200", "16px"),
        [media.above.sm]: {
            marginInlineEnd: "0"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div[role=tablist] .tab-group-inner button[aria-selected=true]": {
        borderColor: token("color.border.selected"),
        color: token("color.border.selected"),
        backgroundColor: token("color.background.selected")
    }
});

const HeroTabs = () => {
    const tabs = useTabs();
    return (
        <div css={HeroTabStyles}>
            <TabGroup
                tabContentType="custom"
                id="jsm-use-case-hero-tabs"
                dataProductKey="jiraServiceManagement"
                label="Product tour"
                tabs={tabs}
            />
        </div>
    );
};

export default HeroTabs;
